// src/components/shared_components/CreditIndicator.tsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchCreditData } from '@/store/slices/creditSlice';
import { Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover';
import { Coins, AlertCircleIcon } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';

// Include formatting functions directly in the component
const formatNumber = (value: number): string => {
  return new Intl.NumberFormat().format(value);
};

const CreditIndicator: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  
  const { plan, loading } = useAppSelector((state) => state.credit);
  
  useEffect(() => {
    dispatch(fetchCreditData());
    
    // Refresh credits data every 5 minutes
    const interval = setInterval(() => {
      dispatch(fetchCreditData());
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [dispatch]);
  
  const getStatusColor = () => {
    if (!plan) return 'text-gray-400';
    
    const totalCredits = plan.remaining_monthly_credits + plan.extra_credits;
    const percentage = totalCredits / plan.monthly_credits * 100;
    
    if (percentage > 40) return 'text-green-500';
    if (percentage > 10) return 'text-amber-500';
    return 'text-red-500';
  };
  
  const monthlyCreditsRemaining = plan?.remaining_monthly_credits || 0;
  const monthlyCreditsTotal = plan?.monthly_credits || 0;
  const extraCredits = plan?.extra_credits || 0;
  const totalCreditsRemaining = monthlyCreditsRemaining + extraCredits;
  const totalCredits = monthlyCreditsTotal + extraCredits;
    
  const percentUsed = totalCredits > 0 ? 
    100 - (totalCreditsRemaining / totalCredits * 100) : 0;
  
  const hasExtraCredits = extraCredits > 0;
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-9 gap-1 text-sm font-normal"
        >
          <Coins className={`h-4 w-4 ${getStatusColor()}`} />
          <span className="hidden sm:inline">
            {loading ? '...' : formatNumber(totalCreditsRemaining)}
            {hasExtraCredits && <span className="text-xs text-muted-foreground ml-0.5">*</span>}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">{t('credits.availableCredits')}</h4>
            <span className="text-xs text-muted-foreground">
              {plan?.plan_type}
            </span>
          </div>
          
          <Progress value={percentUsed} className="h-2" />
          
          <div className="space-y-3">
            {/* Monthly Credits Row */}
            <div className="flex justify-between text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                <div className="text-xs text-muted-foreground">{t('credits.monthlyCredits')}</div>
              </div>
              <div>
                <span className="font-medium">{formatNumber(monthlyCreditsRemaining)}</span>
                <span className="text-xs text-muted-foreground ml-1">/ {formatNumber(monthlyCreditsTotal)}</span>
              </div>
            </div>
            
            {/* Extra Credits Row */}
            <div className="flex justify-between text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <div className="text-xs text-muted-foreground">{t('credits.extraCredits')}</div>
              </div>
              <div className="font-medium">{formatNumber(extraCredits)}</div>
            </div>
            
            {/* Total Row */}
            <div className="flex justify-between text-sm pt-2 border-t">
              <div className="font-medium">{t('credits.totalAvailable')}</div>
              <div>
                <span className="font-bold">{formatNumber(totalCreditsRemaining)}</span>
                <span className="text-xs text-muted-foreground ml-1">/ {formatNumber(totalCredits)}</span>
              </div>
            </div>
          </div>
          
          
          {totalCreditsRemaining < 1000 && (
            <div className="flex items-center text-sm text-red-500">
              <AlertCircleIcon className="h-4 w-4 mr-1" />
              {t('credits.lowCreditsWarning')}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CreditIndicator;